'use client';

import { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceDetail {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  features: string[];
  technicalSpecs: string[];
  caseStudies: Array<{
    title: string;
    client: string;
    result: string;
  }>;
  capabilities: string[];
  responseTime: string;
  coverage: string;
  certifications: string[];
}

interface ServiceDetailModalProps {
  service: ServiceDetail | null;
  onClose: () => void;
}

export default function ServiceDetailModal({
  service,
  onClose,
}: ServiceDetailModalProps) {
  useEffect(() => {
    if (service) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [service]);

  if (!service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-headline font-bold text-primary">
            {service.title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-colors duration-300"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} className="text-textPrimary" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="relative h-64 rounded-lg overflow-hidden">
            <AppImage
              src={service.image}
              alt={service.alt}
              className="w-full h-full object-cover"
            />
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-primary mb-3">
              Service Overview
            </h3>
            <p className="text-sm font-body text-textPrimary leading-relaxed">
              {service.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-headline font-bold text-primary mb-3 flex items-center">
                <Icon name="CheckCircleIcon" size={20} className="mr-2" />
                Key Features
              </h3>
              <ul className="space-y-2">
                {service.features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-start space-x-2 text-sm font-body text-textPrimary"
                  >
                    <Icon
                      name="ChevronRightIcon"
                      size={16}
                      className="text-secondary mt-0.5 flex-shrink-0"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-headline font-bold text-primary mb-3 flex items-center">
                <Icon name="CogIcon" size={20} className="mr-2" />
                Technical Specifications
              </h3>
              <ul className="space-y-2">
                {service.technicalSpecs.map((spec, index) => (
                  <li
                    key={index}
                    className="flex items-start space-x-2 text-sm font-body text-textPrimary"
                  >
                    <Icon
                      name="ChevronRightIcon"
                      size={16}
                      className="text-secondary mt-0.5 flex-shrink-0"
                    />
                    <span>{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-primary mb-3 flex items-center">
              <Icon name="DocumentTextIcon" size={20} className="mr-2" />
              Case Studies
            </h3>
            <div className="space-y-3">
              {service.caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="bg-muted rounded-lg p-4 border border-border"
                >
                  <h4 className="text-sm font-headline font-bold text-primary mb-1">
                    {study.title}
                  </h4>
                  <p className="text-xs font-body text-textSecondary mb-2">
                    Client: {study.client}
                  </p>
                  <p className="text-xs font-body text-textPrimary">
                    Result: {study.result}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-headline font-bold text-primary mb-3 flex items-center">
              <Icon name="ShieldCheckIcon" size={20} className="mr-2" />
              Capabilities & Certifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-headline font-semibold text-textPrimary mb-2">
                  Core Capabilities
                </h4>
                <ul className="space-y-1">
                  {service.capabilities.map((capability, index) => (
                    <li
                      key={index}
                      className="text-xs font-body text-textSecondary flex items-center space-x-2"
                    >
                      <Icon name="CheckIcon" size={14} className="text-success" />
                      <span>{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-headline font-semibold text-textPrimary mb-2">
                  Certifications
                </h4>
                <ul className="space-y-1">
                  {service.certifications.map((cert, index) => (
                    <li
                      key={index}
                      className="text-xs font-body text-textSecondary flex items-center space-x-2"
                    >
                      <Icon name="CheckBadgeIcon" size={14} className="text-accent" />
                      <span>{cert}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-4 border border-border">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="ClockIcon" size={16} className="text-secondary" />
                  <span className="text-xs font-headline font-semibold text-textPrimary">
                    Response Time
                  </span>
                </div>
                <p className="text-sm font-body text-primary font-bold">
                  {service.responseTime}
                </p>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="MapPinIcon" size={16} className="text-secondary" />
                  <span className="text-xs font-headline font-semibold text-textPrimary">
                    Coverage
                  </span>
                </div>
                <p className="text-sm font-body text-primary font-bold">
                  {service.coverage}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button className="flex-1 px-6 py-3 bg-cta text-cta-foreground rounded-md text-sm font-cta font-semibold shadow-cta transition-all duration-300 hover:-translate-y-0.5 hover:shadow-modal flex items-center justify-center">
              <Icon name="PhoneIcon" size={18} className="mr-2" />
              Request Service Quote
            </button>
            <button className="flex-1 px-6 py-3 bg-secondary text-secondary-foreground rounded-md text-sm font-cta font-semibold transition-all duration-300 hover:bg-primary flex items-center justify-center">
              <Icon name="DocumentArrowDownIcon" size={18} className="mr-2" />
              Download Specifications
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}